/**
 * Franchise Partner Search Field Component
 * Autocomplete search with FP status and country chips
 */

import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { franchisePartnersApi, FranchisePartnerSearchResult, handleApiError } from '../../../../services/api'
import { getFpStatusColor } from '../../../../utils/tableFormatters'
import { highlightMatch } from '../../../../utils/highlightMatch'

interface FranchisePartnerSearchFieldProps {
  value: string
  onChange: (fpCode: string) => void
  placeholder?: string
  label?: string
  disabled?: boolean
  minSearchLength?: number
  statusFilter?: string
}

const labelStyle = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.75rem',
  mb: 0.5,
  display: 'block',
}

const getCountryColor = (country: string): 'primary' | 'info' | 'error' | 'default' => {
  switch (country?.toUpperCase()) {
    case 'AU':
      return 'primary'  // Teal
    case 'NZ':
      return 'info'     // Blue
    case 'USA':
    case 'US':
      return 'error'    // Red
    default:
      return 'default'
  }
}

const FranchisePartnerSearchField: React.FC<FranchisePartnerSearchFieldProps> = ({
  value,
  onChange,
  placeholder = 'Search by name or FP code',
  label = 'Search for Franchise Partners',
  disabled = false,
  minSearchLength = 2,
  statusFilter,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<FranchisePartnerSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<FranchisePartnerSearchResult | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear input and selection when external value is cleared
  useEffect(() => {
    if (!value) {
      setInputValue('')
      setSelectedOption(null)
    } else {
      // When value is set externally, clear inputValue (chips will show instead)
      setInputValue('')
    }
  }, [value])

  // Fetch FP details when value is set externally (e.g., from URL)
  useEffect(() => {
    const fetchFpDetails = async () => {
      if (value && !selectedOption) {
        try {
          // Search for the FP by code to get full details
          const response = await franchisePartnersApi.search({ q: value })
          const match = response.data.find(fp => fp.fp_code === value)
          if (match) {
            setSelectedOption(match)
          }
        } catch (error) {
          console.error('Failed to fetch FP details:', error)
        }
      }
    }
    fetchFpDetails()
  }, [value]) // Only on value change, not selectedOption

  const searchFranchisePartners = useCallback(async (query: string) => {
    if (query.length < minSearchLength) {
      setOptions([])
      return
    }

    try {
      setLoading(true)
      const params: { q: string; status?: string } = { q: query }
      if (statusFilter) {
        params.status = statusFilter
      }
      const response = await franchisePartnersApi.search(params)
      setOptions(response.data)
    } catch (error) {
      console.error('Failed to search franchise partners:', handleApiError(error))
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [minSearchLength, statusFilter])

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      setInputValue(newInputValue)

      // Clear previous debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      // If user cleared input, also clear the selection
      if (reason === 'clear' || newInputValue === '') {
        onChange('')
        setOptions([])
        setSelectedOption(null)
        return
      }

      // If user is typing (not just selecting), clear selection and search
      if (reason === 'input') {
        setSelectedOption(null)
        debounceRef.current = setTimeout(() => {
          searchFranchisePartners(newInputValue)
        }, 300)
      }
    },
    [onChange, searchFranchisePartners]
  )

  const handleSelectionChange = useCallback(
    (_event: React.SyntheticEvent, option: FranchisePartnerSearchResult | string | null) => {
      if (!option) {
        onChange('')
        setSelectedOption(null)
        return
      }

      // Handle both object selection and free text
      if (typeof option === 'string') {
        onChange(option)
        setSelectedOption(null)
      } else {
        onChange(option.fp_code)
        setSelectedOption(option)
      }
      setOpen(false)
    },
    [onChange]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <Box>
      <Typography component="label" sx={labelStyle}>
        {label}
      </Typography>
      <Autocomplete
        freeSolo
        autoHighlight
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        loading={loading}
        disabled={disabled}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSelectionChange}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.franchise_name
        }
        isOptionEqualToValue={(option, val) =>
          typeof val === 'string' ? option.fp_code === val : option.fp_code === val.fp_code
        }
        filterOptions={(x) => x}
        noOptionsText={
          inputValue.length > 0 && inputValue.length < minSearchLength
            ? `Type at least ${minSearchLength} characters`
            : 'No franchise partners found'
        }
        loadingText="Searching..."
        renderOption={(props, option) => {
          const isActive = option.fp_status === 'Active'
          return (
            <Box
              component="li"
              {...props}
              key={option.fp_code}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                opacity: isActive ? 1 : 0.6,
                '&:hover': { opacity: 1 },
              }}
            >
              <Chip
                label={option.country === 'USA' ? 'US' : option.country}
                size="small"
                variant="outlined"
                color={getCountryColor(option.country)}
                sx={{ minWidth: 36 }}
              />
              <Typography
                variant="body2"
                fontWeight={isActive ? 500 : 400}
                noWrap
                sx={{
                  flex: 1,
                  color: isActive ? 'text.primary' : 'text.secondary',
                }}
              >
                {highlightMatch(option.franchise_name, inputValue)}
              </Typography>
              {option.fp_status && (
                <Chip
                  label={option.fp_status}
                  size="small"
                  color={getFpStatusColor(option.fp_status)}
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={selectedOption ? '' : placeholder}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedOption ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                  <Chip
                    label={selectedOption.country === 'USA' ? 'US' : selectedOption.country}
                    size="small"
                    variant="outlined"
                    color={getCountryColor(selectedOption.country)}
                    sx={{ height: 22 }}
                  />
                  <Typography variant="body2" fontWeight={500} sx={{ mx: 0.5 }}>
                    {selectedOption.franchise_name}
                  </Typography>
                  <Chip
                    label={selectedOption.fp_status}
                    size="small"
                    color={getFpStatusColor(selectedOption.fp_status || '')}
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                </Box>
              ) : (
                <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-listbox': {
            maxHeight: 300,
          },
        }}
      />
    </Box>
  )
}

export default FranchisePartnerSearchField
